import type {TextToApplyNode as TextToApplyNodeType} from 'src/types/chatbot';
import type {NodeEditorProps} from './node-editor-v2';

import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {useChatbotStateContext} from '../chatbot-state-context';

import {
  createTextToApplyNode,
  createEditableGraph,
  findAncestorNodeVariables,
  getHasAncestorJobMatchQuestionNode,
  getIsUsingDiscoverAttribute,
} from '../utils';

import {useReleaseFlag} from 'src/hooks/product-flags';

import {validateNode} from '../node-editor/validation';

import {MixVariableInput} from '../common';

import {Button} from 'src/designSystem2021Components/buttons';

import {
  MessageDraftEditor,
  upgradePossibleEntitiesInRDCS,
} from 'src/components/conversation-builder/draft-editor';

import {Input} from 'src/designSystem2021Components/input';
import {SubTitleExtraSmall} from '@spaced-out/ui-design-system/lib/components/Text';

import WarningIcon from 'src/images/icons/alert-modal.svg';

import {useEditorState, useChatbotVariables} from '../node-editor/reducer';

import css from './node-editor-v2.css';
import {useSelector} from 'react-redux';
import {selectEntityTypeMappingsByName} from 'src/selectors/ats-entities';
import RichEditorWithWarning from 'src/components/conversation-builder/lib/rich-editor-with-warning/rich-editor-with-warning';
import {getDiscoverAttributeWarningMessage} from 'src/components/conversation-builder/constants';
import {type RawDraftContentState, type DraftDecoratorType} from 'draft-js';


export default function TextToApplyNode({
  graph,
  onCancel,
  readOnly,
  isSavingNode,
  setIsSavingNode,
}: NodeEditorProps) {
  const {t} = useLabelTranslation();
  const richTextEditorEnabled = useReleaseFlag('chatbotFlowRichEditor');
  const richTextEditorFormattingEnabled = useReleaseFlag(
    'chatbotFlowRichEditorFormatting',
  );
  const chatbotNewHyperlinkModalEnabled = useReleaseFlag(
    'chatbotNewHyperlinkModal',
  );
  const atsMappings = useSelector(selectEntityTypeMappingsByName);
  const {state, dispatch} = useChatbotStateContext();

  const [hasSubmitted, setHasSubmitted] = React.useState(false);

  const editingNodeData = graph.nodes.find(({id}) => id === state.editingNode);
  const defaultNode =
    state.editingNode !== null && editingNodeData?.type === 'text_to_apply'
      ? editingNodeData
      : createTextToApplyNode();

  const edgeId = Number(state.insertingNode?.edgeId || state.editingNode);

  const [editorState, editorDispatch] = useEditorState<TextToApplyNodeType>({
    defaultNode,
  });

  const {node} = editorState;

  const setNode = (payload: TextToApplyNodeType) => {
    editorDispatch({type: 'update', payload});
  };

  const editableGraph = createEditableGraph(graph);

  const nodeVariables = findAncestorNodeVariables(
    editableGraph,
    defaultNode,
    edgeId,
  );

  const mappedNodeVariables = nodeVariables.map((nodeVar) => ({
    ...nodeVar,
    key: nodeVar.node_id,
    label: nodeVar.name,
  }));

  const hasAncestorJobMatchQuestionNode = getHasAncestorJobMatchQuestionNode(
    editableGraph,
    defaultNode,
    state.insertingNode?.edgeId,
  );

  const dynamicLabels = useChatbotVariables(graph.anchor_entity_type, {
    showDiscoverAttributes: hasAncestorJobMatchQuestionNode,
    showExternalVariables: Boolean(graph.flow_config.api_bot_enabled),
  });

  const validation = validateNode(node, editableGraph);
  const error = hasSubmitted ? validation : null;

  const handleSaveNode = () => {
    if (validation) {
      setHasSubmitted(true);
    } else {
      state.insertingNode
        ? dispatch({
            type: 'COMMIT_NODE',
            payload: node,
          })
        : dispatch({
            type: 'UPDATE_NODE',
            payload: node,
          });
    }
  };

  React.useEffect(() => {
    if (isSavingNode) {
      handleSaveNode();
      setIsSavingNode(false);
    }
  }, [isSavingNode]);

  const prepareContentOnLoad = (
    rdcs: RawDraftContentState,
    decorator: DraftDecoratorType,
  ) =>
    upgradePossibleEntitiesInRDCS(
      rdcs,
      editableGraph,
      nodeVariables,
      dynamicLabels,
      decorator,
    );

  const dropdownOptionGroups = [
    {
      groupTitle: '',
      groupType: '',
      options: graph.text_to_apply_config.keywords.map((keyword) => ({
        value: keyword,
        label: keyword,
      })),
    },
  ];

  const handleSelectToken = (label: string, value: string, type: string) => {
    const newAnswers = [...(node.answers ?? [])];
    if (!newAnswers.find((answer) => answer.value === value)) {
      newAnswers.push({label, value, id: value});
    }
    setNode({...node, answers: newAnswers});
  };

  // @ts-expect-error TS2304
  const handleRemoveToken = (token: MixVariableToken) => {
    const newAnswers = [...(node.answers ?? [])];
    setNode({
      ...node,
      answers: newAnswers.filter(({value}) => token.value !== value),
    });
  };

  const unusedKeywords = (node.answers ?? [])
    // @ts-expect-error - TS2345 - Argument of type 'string | boolean' is not assignable to parameter of type 'string'.
    .filter(({value}) => !graph.text_to_apply_config.keywords.includes(value))
    .map(({value}) => value);

  const isUsingDiscoverAttribute = getIsUsingDiscoverAttribute(
    node.text_draft_json,
  );

  const DISCOVER_ATTRIBUTE_WARNING_MESSAGE =
    getDiscoverAttributeWarningMessage(t);

  return (
    <div className={css.questionNodeContainer}>
      <div className={css.section}>
        <Input
          label={t('Node_Title', 'Node Title')}
          value={node.name}
          onChange={(event) =>
            setNode({...node, name: event.currentTarget.value})
          }
        />
        {error?.name && (
          <div className={css.errorText}>
            {error.name
              ? error.name.errors?.includes('EMPTY')
                ? `${t(
                    'This_node_must_have_a_name',
                    'This node must have a name',
                  )}.`
                : `${t(
                    'This_name_is_already_taken',
                    'This name is already taken',
                  )}.`
              : `${t(
                  'Node_names_must_be_unique_40_characters_maximum',
                  'Node names must be unique. 40 characters maximum.',
                )}.`}
          </div>
        )}
      </div>
      <div className={css.section}>
        <div className={css.inputLabel}>{`${t('Question', 'Question')}*`}</div>
        {richTextEditorEnabled ? (
          <RichEditorWithWarning
            warningMessage={
              isUsingDiscoverAttribute ? DISCOVER_ATTRIBUTE_WARNING_MESSAGE : ''
            }
            defaultValue={node.text_draft_json}
            onChange={(text_draft_json) => {
              setNode({...node, text_draft_json});
            }}
            editorHeight="200px"
            includeFormatting={Boolean(richTextEditorFormattingEnabled)}
            includeHyperlink={!chatbotNewHyperlinkModalEnabled}
            includeHyperlinkV2={Boolean(chatbotNewHyperlinkModalEnabled)}
            includeGif
            // @ts-expect-error - TS2322 - Type '{ key: number; label: string; name: string; node_id: number; node_name: string; derived_field_name?: string | undefined; template: string; node_type?: string | undefined; attribute_name?: string | undefined; }[]' is not assignable to type 'MenuOption[]'.
            nodeVariables={mappedNodeVariables}
            atsVariables={
              graph.use_case === 'sourcing' ? undefined : dynamicLabels
            }
            readOnly={readOnly}
            entityType={graph.anchor_entity_type}
            prepareContentOnLoad={prepareContentOnLoad}
            atsTooltip={
              hasAncestorJobMatchQuestionNode
                ? `${t('ATS_External_variables', 'ATS & External variables')}`
                : `${t('ATS_variables', 'ATS variables')}`
            }
            enableDraftJSONCopy
          />
        ) : (
          <MessageDraftEditor
            placeholder={`${t('Enter_a_message', 'Enter a message')}...`}
            defaultValue={node.text_draft_json}
            onChange={(text_draft_json) => setNode({...node, text_draft_json})}
            entityType={graph.anchor_entity_type}
            isFirstSmsMessage={false}
            label=""
            extraCharacterCount={0}
            useCase={graph.use_case}
            nodeVariables={nodeVariables}
            readOnly={readOnly}
            showDiscoverAttributes={hasAncestorJobMatchQuestionNode}
            showExternalVariables={Boolean(graph.flow_config.api_bot_enabled)}
            warningMessage={
              isUsingDiscoverAttribute ? DISCOVER_ATTRIBUTE_WARNING_MESSAGE : ''
            }
          />
        )}
        <SubTitleExtraSmall color="information">{`${t(
          'This_question_will_appear_only_when_a_candidate_types_restart_prompting_them_to_select_a_branch_on_this_node_This_ensures_candidates_can_easily_navigate_to_any_other_branch_if_they_wish_to_do_so',
          'This question will appear only when a candidate types "restart," prompting them to select a branch on this node. This ensures candidates can easily navigate to any other branch if they wish to do so',
        )}.`}</SubTitleExtraSmall>

        {error?.text_draft_json && (
          <div className={css.errorText}>{`${t(
            'Message_cannot_be_empty',
            'Message cannot be empty',
          )}.`}</div>
        )}
      </div>
      <div className={css.section}>
        <div className={css.inputLabel}>{`${t(
          'Text_to_apply_code_words',
          'Text to apply code words',
        )}`}</div>
        <div className={css.mixVariableInputWrapper}>
          <MixVariableInput
            dropdownOptionGroups={dropdownOptionGroups}
            // @ts-expect-error - TS2322 - Type 'Answer[]' is not assignable to type 'MixVariableToken[]'.
            values={node.answers ?? []}
            handleSelectToken={handleSelectToken}
            handleRemoveToken={handleRemoveToken}
            allowArbitraryValues
            disabled={readOnly}
            classNames={{dropdown: css.textToApplyMixVarDropdown}}
          />
        </div>
        {error?.answers && (
          <div className={css.errorText}>
            {`${t(
              'Select_at_least_2_text_to_apply_code_words',
              'Select at least 2 text to apply code words',
            )}.`}
          </div>
        )}
        {unusedKeywords.length > 0 && (
          <div className={css.warning}>
            <WarningIcon className={css.warningIcon} />
            <p>
              <span>{`${t(
                'These_code_words_have_not_been_added_to',
                'These code words have not been added to',
              )} `}</span>
              <a
                href={`/conversations/${graph.id}/settings#textToApply`}
                className={css.clickable}
              >
                {`${t('Text_to_Apply_settings', 'Text to Apply settings')}`}
              </a>

              <span>{`: ${unusedKeywords.join(', ')}`}</span>
            </p>
          </div>
        )}
        {/* TODO: we may also want to add a warning for keywords added that are used in other flows */}
      </div>
      <div className={css.footer}>
        <Button size="small" type="secondary" onClick={onCancel}>
          {`${t('Cancel', 'Cancel')}`}
        </Button>
        <Button
          size="small"
          type="primary"
          onClick={handleSaveNode}
          disabled={hasSubmitted && !!validation}
        >
          {`${t('Save', 'Save')}`}
        </Button>
      </div>
    </div>
  );
}
